import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { scaleLinear, scaleUtc } from "@visx/scale";
import {
  AreaClosed,
  Bar,
  BarRounded,
  Circle,
  Line,
  LinePath,
} from "@visx/shape";
import { useTooltip } from "@visx/tooltip";
import { extent, max, min } from "d3-array";
import sortedIndexBy from "lodash/sortedIndexBy";
import React, { useCallback } from "react";
import { BAR_COLOR, BAR_WIDTH, GRID_COLOR, LINE_COLOR } from "./ChartConstants";

export type DataPoint = {
  timestamp: string;
  value: number;
};

export function SparkChart({
  type = "line",
  points,
  width,
  height,
  lineStartColor = LINE_COLOR,
  lineEndColor = LINE_COLOR,
  barColor = BAR_COLOR,
  handlePointChange,
  showPointerLine,
}: {
  type?: "line" | "bar";
  width: number;
  height: number;
  points: DataPoint[];
  lineStartColor?: string;
  lineEndColor?: string;
  barColor?: string;
  handlePointChange?: (point: DataPoint | null) => void;
  // This is a vertical line that tracks the current mouse pointer
  showPointerLine?: boolean;
}) {
  const getX = (val: DataPoint) => new Date(val.timestamp);
  const getY = (val: DataPoint) => val.value || 0;

  // scales
  const xScale = scaleUtc<number>({
    domain: extent(points, getX) as [Date, Date],
    nice: false,
  }).range([0, width - 8]);

  const xBarScale = scaleUtc<number>({
    domain: extent(points, getX) as [Date, Date],
    nice: false,
  }).range([0, width - 8 - BAR_WIDTH]);

  const yScale = scaleLinear<number>({
    domain: [min(points, getY) as number, max(points, getY) as number],
  }).range([height - 8, 0]);

  const { showTooltip, hideTooltip, tooltipData, tooltipOpen } = useTooltip<{
    point: DataPoint;
    xAt: number;
    yAt: number;
    dataIdx: number;
  }>();

  const handleTooltip = useCallback(
    (
      event:
        | React.TouchEvent<SVGRectElement>
        | React.MouseEvent<SVGRectElement>,
    ) => {
      const { x } = localPoint(event) || { x: 0 };
      const x_cursor: Date = xScale.invert(x);

      const { point, idx } = findClosestPoint({
        points,
        value: x_cursor.toISOString(),
        attr: "timestamp",
        converter: (value) => new Date(value).getTime(),
      });

      const yAt = yScale(point.value);
      const scaledX = type === "bar" ? xBarScale : xScale;
      let xAt = scaledX(new Date(point.timestamp));

      if (type === "bar") {
        xAt += BAR_WIDTH / 2;
      }

      showTooltip({
        tooltipData: { point, xAt, yAt, dataIdx: idx },
        tooltipLeft: xAt,
        tooltipTop: height / 2 - 40,
      });
      handlePointChange?.(point);
    },
    [
      showTooltip,
      handlePointChange,
      height,
      xScale,
      xBarScale,
      type,
      points,
      yScale,
    ],
  );

  return (
    <div className="chart-container">
      <svg width={width} height={height}>
        <Group top={4} left={4}>
          {type === "line" && (
            <>
              <LinearGradient
                id="line-gradient"
                from={lineStartColor}
                to={lineEndColor}
              />

              <LinearGradient
                id="area-gradient"
                from={lineStartColor}
                fromOpacity={0.15}
                to={lineEndColor}
                toOpacity={0}
              />

              <AreaClosed<DataPoint>
                data={points}
                x={(d) => xScale(getX(d))}
                y={(d) => yScale(getY(d))}
                yScale={yScale}
                strokeWidth={1}
                stroke="url(#area-gradient)"
                fill="url(#area-gradient)"
                curve={curveMonotoneX}
              />

              <LinePath<DataPoint>
                curve={curveMonotoneX}
                data={points}
                x={(d) => xScale(getX(d))}
                y={(d) => yScale(getY(d))}
                stroke="url(#line-gradient)"
                strokeWidth={2}
              />
            </>
          )}

          {type === "bar" &&
            points.map((point) => {
              const x = xBarScale(getX(point));
              const y = yScale(getY(point));
              return (
                <BarRounded
                  key={point.timestamp}
                  fill={barColor}
                  width={BAR_WIDTH}
                  radius={BAR_WIDTH / 2}
                  x={x}
                  y={y}
                  height={height - y - 4}
                  top
                  bottom
                />
              );
            })}

          {tooltipOpen && tooltipData && (
            <>
              <Line
                from={{ x: tooltipData.xAt, y: 0 }}
                to={{
                  x: tooltipData.xAt,
                  y: height,
                }}
                strokeWidth={2}
                strokeDasharray="4"
                stroke={GRID_COLOR}
              />
              <Circle
                cx={tooltipData.xAt}
                cy={tooltipData.yAt}
                fill="var(--tertiary-color)"
                stroke="var(--primary-bg-color)"
                strokeWidth={2}
                r={5}
              />
            </>
          )}
        </Group>

        {showPointerLine && (
          // This is used as a surface to listen to tooltip changes
          <Bar
            fill="transparent"
            x={0}
            y={0}
            width={width}
            height={height}
            rx={14}
            onMouseLeave={() => {
              hideTooltip();
              handlePointChange?.(null);
            }}
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
          />
        )}
      </svg>

      <style jsx>{`
        .chart-container {
          position: relative;
          overflow: hidden;

          svg {
            width: ${width}px;
            height: ${height}px;
          }
        }

        .chart-tooltip {
          .fs-xs,
          .count {
            white-space: nowrap;
          }

          .fs-xs {
            font-weight: var(--font-weight-medium);
          }

          .count {
            margin-top: 0.25rem;
            font-weight: var(--font-weight-bold);

            b {
              font-size: 1rem;
            }
          }
        }
      `}</style>
    </div>
  );
}

function findClosestPoint<
  TPoint extends { [attr: string]: TValue },
  TAttribute extends string,
  TValue,
>({
  points,
  value,
  attr,
  converter,
}: {
  points: TPoint[];
  value: TPoint[TAttribute];
  attr: TAttribute;
  converter?: (value: TPoint[TAttribute]) => number;
}): { point: TPoint; idx: number } {
  let idx = Math.min(
    sortedIndexBy(
      points,
      { [attr]: value } as { [key: string]: TValue },
      (row) => row[attr],
    ),
    points.length - 1,
  );

  if (!converter) {
    converter = (v) => {
      return Number(v);
    };
  }

  let point = points[idx];
  const numberValue = converter(value);

  if (idx > 0) {
    const altIdx = idx - 1;
    const altPoint = points[altIdx];

    // @ts-ignore - we should type the point[attr] call
    const distToPoint = Math.abs(converter(point[attr]) - numberValue);

    // @ts-ignore - we should type the point[attr] call
    const distToAltPoint = Math.abs(converter(altPoint[attr]) - numberValue);

    if (distToAltPoint < distToPoint) {
      point = altPoint;
      idx = altIdx;
    }
  }

  return { point, idx };
}
